import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ja";
import React, { useCallback, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  BASIC_BROWN,
  BASIC_DARK_GRAY,
  BASIC_GRAY,
  BASIC_LINE,
  BASIC_WHITE,
} from "../../constants/colors";
import IconArrowRight from "../../icons/IconArrowRight";
import { dateText } from "../../utils/date";
import { Button } from "./Button";

dayjs.locale("ja");

type Props = {
  value?: Date | Dayjs | string;
  minDate?: Date | Dayjs | string;
  maxDate?: Date | Dayjs | string;
  onConfirm?: (date: Dayjs) => void;
  onClose?: () => void;
  allowOutOfMonthSelection?: boolean; // default true
  defaultMinToToday?: boolean; // default true
};

export const Calendar: React.FC<Props> = ({
  value,
  minDate,
  maxDate,
  onConfirm,
  onClose,
  allowOutOfMonthSelection = true,
  defaultMinToToday = true,
}) => {
  const initial = useMemo(() => {
    if (value) return dayjs(value);
    if (minDate) return dayjs(minDate).startOf("day");
    return dayjs().startOf("day");
  }, [value, minDate]);

  const [cursorMonth, setCursorMonth] = useState<Dayjs>(
    initial.startOf("month"),
  );
  const [selectedDate, setSelectedDate] = useState<Dayjs>(
    initial.startOf("day"),
  );

  const min = useMemo(
    () =>
      (minDate
        ? dayjs(minDate)
        : defaultMinToToday
          ? dayjs()
          : undefined
      )?.startOf("day"),
    [minDate, defaultMinToToday],
  );
  const max = useMemo(
    () => (maxDate ? dayjs(maxDate).startOf("day") : undefined),
    [maxDate],
  );

  const weeks = useMemo(() => buildMonthMatrix(cursorMonth), [cursorMonth]);

  const canGoPrev = useMemo(() => {
    if (!min) return true;
    return cursorMonth.isAfter(min, "month");
  }, [cursorMonth, min]);
  const canGoNext = useMemo(() => {
    if (!max) return true;
    return cursorMonth.isBefore(max, "month");
  }, [cursorMonth, max]);

  const shouldDisableConfirm = useMemo(() => {
    if (!selectedDate) return true;
    if (max && selectedDate.isAfter(max)) return true;
    if (min && selectedDate.isBefore(min)) return true;
    return false;
  }, [selectedDate]);

  const isDisabled = (d: Dayjs) => {
    const beforeMin = !!min && d.isBefore(min, "day");
    const afterMax = !!max && d.isAfter(max, "day");
    // do not disable out-of-month days if selection is allowed
    return beforeMin || afterMax;
  };

  const handleConfirm = useCallback(() => {
    onConfirm?.(selectedDate);
    onClose?.();
  }, [onConfirm, onClose, selectedDate]);

  return (
    <CalendarContainer>
      <Header>
        <NavButton
          aria-label="previous month"
          disabled={!canGoPrev}
          $isRight={true}
          onClick={() => setCursorMonth((m) => m.subtract(1, "month"))}
        >
          <IconArrowRight />
        </NavButton>
        <HeaderTitle>
          <YearText>{cursorMonth.format("YYYY年")}</YearText>{" "}
          {cursorMonth.format("M月")}
        </HeaderTitle>
        <NavButton
          aria-label="next month"
          disabled={!canGoNext}
          onClick={() => setCursorMonth((m) => m.add(1, "month"))}
        >
          <IconArrowRight />
        </NavButton>
      </Header>

      <WeekRow>
        {dateText.map((w) => (
          <Weekday key={w}>{w}</Weekday>
        ))}
      </WeekRow>

      <Grid>
        {weeks.map((week, wi) => (
          <Row key={wi}>
            {week.map((d) => {
              const disabled = isDisabled(d);
              const selected = d.isSame(selectedDate, "day");
              const outMonth = d.month() !== cursorMonth.month();
              return (
                <Cell key={d.toString()}>
                  <DayButton
                    onClick={() => {
                      if (disabled) return;
                      if (!allowOutOfMonthSelection && outMonth) return;
                      setSelectedDate(d);
                      if (outMonth && allowOutOfMonthSelection) {
                        setCursorMonth(d.startOf("month"));
                      }
                    }}
                    disabled={disabled}
                    $selected={selected}
                    $outMonth={outMonth}
                  >
                    {d.date()}
                  </DayButton>
                </Cell>
              );
            })}
          </Row>
        ))}
      </Grid>

      <ConfirmWrap>
        <Button
          btnStyle="primary"
          text="設定"
          onClick={handleConfirm}
          disabled={shouldDisableConfirm}
          rounded="md"
        />
      </ConfirmWrap>
    </CalendarContainer>
  );
};

function buildMonthMatrix(month: Dayjs): Dayjs[][] {
  const start = month.startOf("month");
  const end = month.endOf("month");
  // Calendar starts on Sunday
  const firstCell = start.startOf("week");
  const lastCell = end.endOf("week");

  const days: Dayjs[] = [];
  let cursor = firstCell;
  while (cursor.isBefore(lastCell) || cursor.isSame(lastCell, "day")) {
    days.push(cursor);
    cursor = cursor.add(1, "day");
  }

  const rows: Dayjs[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    rows.push(days.slice(i, i + 7));
  }
  return rows;
}

const CalendarContainer = styled.div`
  background-color: ${BASIC_WHITE};
  border-radius: 8px;
  padding: 20px 40px;
  box-shadow: 0px 0px 3px 0px #00000029;
  animation: slideDown 0.2s ease-out;
  transition: box-shadow 0.3s ease-in-out;

  &:hover {
    box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.15);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  transition: all 0.2s ease-in-out;
`;

const HeaderTitle = styled.div`
  font-weight: 600;
  font-size: 20px;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: ${BASIC_BROWN};
  }
`;

const YearText = styled.span`
  font-weight: normal;
  color: ${BASIC_GRAY};
  transition: color 0.2s ease-in-out;
`;

const NavButton = styled.button<{ $isRight?: boolean }>`
  appearance: none;
  border: none;
  background: transparent;
  font-size: 20px;
  padding: 6px 10px;
  transform: ${(p) => (p.$isRight ? "rotate(180deg)" : "none")};
  color: ${BASIC_BROWN};
  opacity: ${(p) => (p.disabled ? 0.3 : 1)};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-radius: 30%;

  &:disabled {
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: rgba(139, 69, 19, 0.1);
    transform: ${(p) =>
      p.$isRight ? "rotate(180deg) scale(1.1)" : "scale(1.1)"};
  }

  &:active:not(:disabled) {
    transform: ${(p) =>
      p.$isRight ? "rotate(180deg) scale(0.95)" : "scale(0.95)"};
  }
`;

const WeekRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-bottom: 1.5px solid ${BASIC_LINE};
  padding: 8px 0 12px;
  transition: border-color 0.2s ease-in-out;
`;

const Weekday = styled.div`
  text-align: center;
  color: ${BASIC_GRAY};
  font-size: 12px;
  font-family: Hiragino Sans;
  font-weight: 300;
  line-height: 14px;
  letter-spacing: 0%;
  text-align: center;
  transition: color 0.2s ease-in-out;
`;

const Grid = styled.div`
  display: grid;
  grid-template-rows: repeat(6, 1fr);
  transition: all 0.3s ease-in-out;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  transition: all 0.2s ease-in-out;
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 0;
  font-size: 14px;
  font-family: Hiragino Sans;
  font-weight: 300;
  line-height: 14px;
  letter-spacing: 0%;
  text-align: center;
  transition: all 0.2s ease-in-out;
`;

const DayButton = styled.button<{ $selected: boolean; $outMonth?: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  background: ${(p) => (p.$selected ? BASIC_BROWN : "transparent")};
  color: ${(p) =>
    p.$selected ? "#fff" : p.$outMonth ? BASIC_DARK_GRAY : "inherit"};
  font-weight: 600;
  cursor: pointer;
  opacity: ${(p) => (p.disabled ? 0.35 : 1)};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: ${(p) => (p.$selected ? "16px" : "14px")};
  position: relative;
  overflow: hidden;

  &:disabled {
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: ${(p) => (p.$selected ? BASIC_BROWN : BASIC_GRAY)};
    color: ${(p) => (p.$selected ? "#fff" : BASIC_WHITE)};
    transform: scale(1.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
    transition: transform 0.1s ease-in-out;
  }

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%);
    transition:
      width 0.3s ease-out,
      height 0.3s ease-out;
  }

  &:active:not(:disabled)::before {
    width: 100%;
    height: 100%;
  }
`;

const ConfirmWrap = styled.div`
  margin-top: 18px;
  display: flex;
  justify-content: center;
  transition: all 0.2s ease-in-out;
`;

const slideDown = keyframes`
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;
